/**
 * Consumer multi-item claim page. Reuses the SAME shared MultiItemClaimEditor
 * as Sage, pre-bound to the logged-in customer (no customer capture - the
 * identity is session-derived server-side). The umbrella claim type is read
 * from the ``?type=`` query param (set by the brand-aware entry link).
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { ConsumerShell } from '@/consumer/components/consumer-shell'
import {
  useConsumerRegisterAndClaim,
  useConsumerClaimDisclaimers,
} from '@/consumer/hooks/use-claims'
import { useConsumerProductSearch } from '@/consumer/hooks/use-registration'
import {
  MultiItemClaimEditor,
  type MultiItemClaimSubmitPayload,
} from '@/shared/components/claims/multi-item-claim-editor'
import { type ResourceOption } from '@/shared/components/ui/resource-search-input'
import { toastApiError } from '@/shared/lib/api-error'

export default function ConsumerMultiItemClaimPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const umbrellaKey = params.get('type') || ''

  const [query, setQuery] = useState('')
  // Debounce so typing doesn't fire a request on every keystroke (the search
  // hook is always enabled now that an empty query browses the catalogue).
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => clearTimeout(handle)
  }, [query])
  // Type-ahead picker: the first page of matches is enough here.
  const { data: searchResults } = useConsumerProductSearch(debouncedQuery, 1, 20)
  const productResults: ResourceOption[] = useMemo(
    () =>
      (searchResults?.results ?? []).map((p) => ({
        uuid: p.uuid,
        name: p.cached_short_name || p.cached_long_name || 'Product',
      })),
    [searchResults],
  )

  const register = useConsumerRegisterAndClaim()

  // Tenant-authored cut-hose disclaimer, shown once above the submit button and
  // gating submission when acknowledgement is required (DL-44 - "every consumer
  // surface"). Config-driven: tenants that author no `claim_submit` disclaimer
  // render nothing. The editor manages the acknowledgement state internally
  // (uncontrolled), so no ack state is needed here.
  const { data: disclaimerData } = useConsumerClaimDisclaimers()
  const submitDisclaimer = disclaimerData?.disclaimers?.claim_submit ?? null

  const handleSubmit = async (payload: MultiItemClaimSubmitPayload) => {
    try {
      const result = await register.mutateAsync({
        claim_schema_key: umbrellaKey,
        lines: payload.lines,
        shared_attribute_values: payload.shared_attribute_values,
      })
      toast.success(`Submitted ${result.created} claim(s).`)
      navigate('/claims')
    } catch (err) {
      toastApiError(err, 'Failed to submit claim')
    }
  }

  return (
    <ConsumerShell>
      <div className="px-4 py-8">
        <MultiItemClaimEditor
          title="Claim for several items"
          description="Add each item you're claiming for and submit them together."
          productResults={productResults}
          onProductSearch={setQuery}
          onSubmit={handleSubmit}
          isSubmitting={register.isPending}
          submitLabel="Submit claim"
          onCancel={() => navigate('/wallet')}
          disclaimer={submitDisclaimer}
        />
      </div>
    </ConsumerShell>
  )
}
